import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, BellOff, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../api';

const AccountProfile = () => {
  const { account_login } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTradeId, setExpandedTradeId] = useState(null);

  useEffect(() => {
    fetchAccountData();
    const interval = setInterval(fetchAccountData, 10000);
    return () => clearInterval(interval);
  }, [account_login]);

  const fetchAccountData = async () => {
    try {
      const [accRes, tradesRes] = await Promise.all([
        api.get('/users/accounts'),
        api.get(`/trades?account_login=${account_login}`)
      ]);
      
      const currentAcc = accRes.data.find(a => a.account_login === account_login);
      if (currentAcc) setAccount(currentAcc);
      setTrades(tradesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch account data', error);
      setLoading(false);
    }
  };

  const toggleMute = async () => {
    if (!account) return;
    try {
      await api.put(`/users/accounts/${account_login}/mute`, { mute_telegram: !account.mute_telegram });
      fetchAccountData();
    } catch (error) {
      console.error('Failed to toggle mute status', error);
    }
  };

  if (loading) {
    return <div className="text-white">Loading profile...</div>;
  }

  if (!account) {
    return (
      <div className="text-white">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-4 text-indigo-400 hover:text-indigo-300">
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <p>Account not found.</p>
      </div>
    );
  }

  const isOnline = new Date(account.last_seen).getTime() > Date.now() - 30000;

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">
      <button onClick={() => navigate('/')} className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors">
        <ArrowLeft size={16} /> Back to Dashboard
      </button>

      {/* Profile Header */}
      <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl p-8 border border-slate-700/50 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 shadow-xl">
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-20 h-20 bg-indigo-500/20 rounded-full flex items-center justify-center border-2 border-indigo-500/30">
              <Activity className="w-10 h-10 text-indigo-400" />
            </div>
            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-4 border-slate-900 ${isOnline ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' : 'bg-slate-500'}`}></div>
          </div>
          <div>
            <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400 mb-2">
              {account_login}
            </h1>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${account.role === 'Master' ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
                {account.role} Account
              </span>
              <span className={`text-sm font-medium ${isOnline ? 'text-emerald-400' : 'text-slate-500'}`}>
                {isOnline ? 'Online (Active)' : `Last seen: ${new Date(account.last_seen).toLocaleTimeString()}`}
              </span>
            </div>
          </div>
        </div>

        <button 
          onClick={toggleMute}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all shadow-lg ${
            account.mute_telegram 
              ? 'bg-slate-700 hover:bg-slate-600 text-slate-300 border border-slate-600' 
              : 'bg-indigo-600 hover:bg-indigo-500 text-white border border-indigo-500'
          }`}
        >
          {account.mute_telegram ? <BellOff size={20} /> : <Bell size={20} />}
          {account.mute_telegram ? 'Notifications Muted' : 'Notifications Active'}
        </button>
      </div>

      {/* Trade Log */}
      <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700/50 overflow-hidden shadow-xl">
        <div className="px-6 py-5 border-b border-slate-700/50 bg-slate-800/80">
          <h3 className="text-xl font-bold text-white">Trade History</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wider">
                <th className="px-6 py-4 font-semibold">Ticket</th>
                <th className="px-6 py-4 font-semibold">Time</th>
                <th className="px-6 py-4 font-semibold">Symbol</th>
                <th className="px-6 py-4 font-semibold">Type</th>
                <th className="px-6 py-4 font-semibold">Volume</th>
                <th className="px-6 py-4 font-semibold">Open</th>
                <th className="px-6 py-4 font-semibold">Close</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold text-right">Profit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {trades.map((trade) => (
                <React.Fragment key={trade.id}>
                  <tr 
                    className={`transition-colors cursor-pointer ${expandedTradeId === trade.id ? 'bg-indigo-900/20' : 'hover:bg-slate-800/80'}`}
                    onClick={() => setExpandedTradeId(expandedTradeId === trade.id ? null : trade.id)}
                  >
                    <td className="px-6 py-4 text-indigo-300 font-bold flex items-center gap-2">
                      {trade.strategy_data && (
                        expandedTradeId === trade.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                      {trade.ticket}
                    </td>
                    <td className="px-6 py-4 text-slate-400 text-sm">{new Date(trade.open_time).toLocaleString()}</td>
                    <td className="px-6 py-4 text-white font-bold">{trade.symbol}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${trade.type.toLowerCase() === 'buy' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                        {trade.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-200">{trade.volume}</td>
                    <td className="px-6 py-4 text-slate-200">{trade.open_price}</td>
                    <td className="px-6 py-4 text-slate-400">{trade.close_price || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${trade.status === 'Open' ? 'bg-blue-500/10 text-blue-400' : 'bg-slate-700 text-slate-300'}`}>
                        {trade.status}
                      </span>
                    </td>
                    <td className={`px-6 py-4 text-right font-black ${Number(trade.profit) > 0 ? 'text-emerald-400' : Number(trade.profit) < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                      {trade.status === 'Closed' ? `$${Number(trade.profit).toFixed(2)}` : '-'}
                    </td>
                  </tr>
                  
                  {expandedTradeId === trade.id && trade.strategy_data && (
                    <tr className="bg-indigo-900/10">
                      <td colSpan="9" className="px-8 py-6">
                        <div className="bg-slate-900/80 rounded-xl p-5 border border-indigo-500/20">
                          <h4 className="text-xs font-bold text-indigo-400 mb-4 uppercase tracking-widest">Strategy Parameters at Entry</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {Object.entries(typeof trade.strategy_data === 'string' ? JSON.parse(trade.strategy_data) : trade.strategy_data).map(([key, value]) => (
                              <div key={key} className="bg-slate-800/80 rounded-lg p-3 border border-slate-700/50">
                                <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">{key.replace('_', ' ')}</p>
                                <p className="text-sm font-bold text-white">{Number(value).toFixed(4)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {trades.length === 0 && (
                <tr>
                  <td colSpan="9" className="px-6 py-16 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-800 mb-4">
                      <Activity className="w-8 h-8 text-slate-500" />
                    </div>
                    <p className="text-slate-400 font-medium text-lg">No trades recorded yet</p>
                    <p className="text-slate-500 text-sm mt-1">Trades executed by this account will appear here.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccountProfile;
